import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box
} from '@mui/material';
import { IBankPartner } from './InterfaceBankPartner';
import { ShowAlert } from '../../../Component/ShowAlert';
import BankPartnerServices from '../../../Services/BankPartnerServices';

interface BankPartnerFormProps {
    open: boolean;
    onClose: (state: boolean, action: string) => void;
    selectedBankPartner: IBankPartner | null;
    accessToken: string;
    isEditMode: boolean;
}

interface BankPartnerFormState {
    bankName: string;
    plaidBankName: string;
    loading: boolean;
    errors: {
        bankName: string;
        plaidBankName: string;
    };
}

class BankPartnerForm extends React.Component<BankPartnerFormProps, BankPartnerFormState> {
    constructor(props: BankPartnerFormProps) {
        super(props);

        this.state = {
            bankName: props.isEditMode && props.selectedBankPartner
                ? props.selectedBankPartner.bankName
                : '',
            plaidBankName: props.isEditMode && props.selectedBankPartner
                ? props.selectedBankPartner.plaidBankName
                : '',
            loading: false,
            errors: {
                bankName: '',
                plaidBankName: ''
            }
        };
    }


    validateForm = (): boolean => {
        const { bankName, plaidBankName } = this.state;
        const newErrors = {
            bankName: bankName.trim() ? '' : 'Bank name is required',
            plaidBankName: plaidBankName.trim() ? '' : 'Plaid bank name is required'
        };

        this.setState({ errors: newErrors });
        return !Object.values(newErrors).some(error => error !== '');
    }

    handleBankNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            bankName: e.target.value,
            errors: { ...this.state.errors, bankName: '' }
        });
    }

    handlePlaidBankNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            plaidBankName: e.target.value,
            errors: { ...this.state.errors, plaidBankName: '' }
        });
    }

    handleClose = (state: boolean, action: string) => {
        this.setState({
            bankName: '',
            plaidBankName: '',
            errors: { bankName: '', plaidBankName: '' },
            loading: false
        });
        this.props.onClose(state, action);
    };

    handleSubmit = async () => {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ loading: true });

        try {
            const { bankName, plaidBankName } = this.state;
            const { isEditMode, selectedBankPartner, accessToken } = this.props;

            const postData = {
                bankName: bankName.trim(),
                plaidBankName: plaidBankName.trim()
            };

            let response;

            if (isEditMode && selectedBankPartner) {
                response = await BankPartnerServices.updateBankPartner(
                    accessToken,
                    selectedBankPartner.fintechBankingPartnerId,
                    postData

                );
            } else {
                response = await BankPartnerServices.createBankPartner(
                    accessToken,
                    postData
                );
            }

            if (response.ok) {
                const successMessage = isEditMode
                    ? "Bank partner updated successfully."
                    : "Bank partner created successfully.";

                ShowAlert("Success", successMessage, "success").then(() => {
                    this.props.onClose(true, 'success');
                });
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message ||
                    (isEditMode ? "Failed to update bank partner." : "Failed to create bank partner.");

                ShowAlert("Error", errorMessage, "error");
            }
        } catch (error) {
            console.error("Error saving bank partner:", error);
            ShowAlert("Error", "An unexpected error occurred.", "error");
        } finally {
            this.setState({ loading: false });
        }
    }

    getTitle = (): string => {
        return this.props.isEditMode ? "Edit Bank Partner" : "Add New Bank Partner";
    }

    getSubmitButtonText = (): string => {
        const { loading } = this.state;
        const { isEditMode } = this.props;

        return loading
            ? (isEditMode ? 'Updating...' : 'Creating...')
            : (isEditMode ? 'Update' : 'Create');
    }

    render() {
        const { open } = this.props;
        const { bankName, plaidBankName, loading, errors } = this.state;

        console.log("Selected bank partner from props:", this.props.selectedBankPartner);

        return (
            <Dialog open={open} onClose={() => this.handleClose(false, 'cancel')} maxWidth="sm" fullWidth>
                <DialogTitle>{this.getTitle()}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Bank Name"
                            value={bankName}
                            onChange={this.handleBankNameChange}
                            margin="normal"
                            error={!!errors.bankName}
                            helperText={errors.bankName}
                            required
                            disabled={loading}
                        />
                        <TextField
                            fullWidth
                            label="Plaid Bank Name"
                            value={plaidBankName}
                            onChange={this.handlePlaidBankNameChange}
                            margin="normal"
                            error={!!errors.plaidBankName}
                            helperText={errors.plaidBankName}
                            required
                            disabled={loading}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        onClick={() => this.handleClose(false, 'cancel')}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={this.handleSubmit}
                        variant="contained"
                        color="secondary"
                        disabled={loading}
                    >
                        {this.getSubmitButtonText()}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default BankPartnerForm;